import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Save, Mail, Send, AlertCircle, ShieldCheck } from "lucide-react";

const inpCls = "w-full px-3 py-2.5 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700 bg-white";

export function ChangePasswordCard() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="change-password-form" className="bg-white border border-stone-200 rounded-lg p-5 space-y-4 max-w-xl">
      <div className="flex items-center gap-2 text-emerald-900 font-medium"><ShieldCheck className="w-4 h-4" /> Change Password</div>
      <div className="text-xs text-stone-500">Signed in as <span className="font-mono text-emerald-900">{user?.email}</span></div>
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Current Password</label>
        <input data-testid="cp-current" type={show ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} className={inpCls} required autoComplete="current-password" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">New Password</label>
        <div className="relative">
          <input data-testid="cp-new" type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} className={`${inpCls} pr-10`} required minLength={8} autoComplete="new-password" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Confirm New Password</label>
        <input data-testid="cp-confirm" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inpCls} required minLength={8} autoComplete="new-password" />
      </div>
      <button data-testid="cp-submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Update Password"}
      </button>
    </form>
  );
}

export function EmailSettingsCard() {
  const [form, setForm] = useState({ resend_api_key: "", sender_email: "onboarding@resend.dev" });
  const [pub, setPub] = useState(null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get("/email-settings/admin").then((r) => r.data),
      api.get("/email-settings").then((r) => r.data),
    ]);
    setForm(a); setPub(p);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { await api.put("/email-settings", form); toast.success("Email settings saved"); load(); }
    catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="email-settings-form" className="bg-white border border-stone-200 rounded-lg p-5 space-y-4 max-w-xl">
      <div className="flex items-center gap-2 text-emerald-900 font-medium"><Mail className="w-4 h-4" /> Email (Resend) Settings</div>
      <div className={`px-3 py-2 rounded-md text-sm border ${pub?.configured ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
        {pub?.configured ? "✓ Resend configured — password reset emails will be sent" : "⚠ Resend not configured — reset links will be logged to server"}
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Resend API Key</label>
        <div className="relative">
          <input data-testid="es-key" type={show ? "text" : "password"} value={form.resend_api_key || ""} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} className={`${inpCls} pr-10`} placeholder="re_xxxxxxxxxxxxxxxx" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-stone-500 mt-1">Get a key at <a className="text-emerald-900 underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a>. Free tier: 100 emails/day.</p>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Sender Email</label>
        <input data-testid="es-sender" value={form.sender_email || ""} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} className={inpCls} />
        <p className="text-xs text-stone-500 mt-1">Use <code>onboarding@resend.dev</code> until you verify your own domain in Resend.</p>
      </div>
      <div className="bg-stone-50 border border-stone-200 rounded-md p-3 text-sm flex items-start gap-2">
        <KeyRound className="w-4 h-4 text-emerald-900 mt-0.5" />
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500">Super-admin recipient</div>
          <div className="font-mono text-emerald-900 text-sm">{pub?.super_admin_email}</div>
          <div className="text-xs text-stone-500 mt-1">All password reset emails are delivered here only. (Set via SUPER_ADMIN_EMAIL in .env.)</div>
        </div>
      </div>
      <button data-testid="es-submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Email Settings"}</button>
    </form>
  );
}

export function SecuritySection() {
  return (
    <div className="fade-up">
      <div className="mb-6">
        <h2 className="font-serif-en text-3xl text-emerald-950">Security</h2>
        <p className="text-stone-500 text-sm mt-1">Manage your admin password and password-reset email</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <ChangePasswordCard />
        <EmailSettingsCard />
      </div>
    </div>
  );
}
